import { Alert, Button, Form, Space, Spin } from 'antd'
import { orderEditFields } from '@cims-online/shared'
import CIMSDrawer from 'components/CIMSDrawer'
import EditFieldControl from './components/EditFieldControl'
import { useEditWorkflowRecord } from './hooks/useEditWorkflowRecord'
import type { EditWorkflowRecordDrawerProps } from './types'

/**
 * Presents the resolved fields in stable form order and owns loading, failure,
 * and submission controls. Saving freezes dismissal and inputs; a confirmed
 * save closes the drawer and refreshes the records list.
 *
 * Editing keeps the current status, so the title and primary button use
 * `Edit {{recordName}}`. The advance/revert `customizeActionLabel` setting
 * describes a status transition and does not apply to edits.
 */
function EditWorkflowRecordDrawer(props: EditWorkflowRecordDrawerProps) {
  const edit = useEditWorkflowRecord(props)
  const label = `Edit ${edit.options?.recordName ?? props.recordName}`
  return (
    <CIMSDrawer
      open
      title={label}
      onClose={edit.close}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button disabled={edit.saving} onClick={edit.close}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={edit.saving}
              disabled={!edit.canSubmit}
              onClick={edit.submit}
            >
              {label}
            </Button>
          </Space>
        </div>
      }
    >
      <Alert
        type="info"
        showIcon
        message="Available fields depend on the current status and your permissions. Read-only fields are shown for context."
        style={{ marginBottom: 16 }}
      />
      {edit.loading && <Spin aria-label="Loading edit options" />}
      {edit.options?.reason && (
        <Alert type="warning" message={edit.options.reason} />
      )}
      {edit.failure && (
        <Alert
          type="error"
          showIcon
          message={edit.failure}
          action={
            <Button
              disabled={
                edit.saving ||
                edit.loading ||
                (!!edit.options && !edit.canSubmit)
              }
              onClick={edit.retry}
            >
              Try again
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Form layout="vertical" onFinish={edit.submit}>
        {orderEditFields(edit.options?.fields ?? []).map((field) => (
          <EditFieldControl
            key={field.id}
            field={field}
            value={
              field.input ? edit.values[field.id] ?? null : field.initialValue
            }
            error={
              edit.errors.find(({ path }) => path[0] === field.id)?.message
            }
            disabled={edit.frozen}
            onChange={(value) => edit.change(field.id, value)}
          />
        ))}
      </Form>
    </CIMSDrawer>
  )
}

export default EditWorkflowRecordDrawer
